import { Controller } from '@hotwired/stimulus'

// Type-or-pick input for a vignette variable value.
//
// The `input` target IS the real form field — its `name` is the Rails param — so
// there is no hidden mirror to keep in sync and nothing to serialize on submit.
// Picking a suggestion sets the value and then fires a native `input` event, so
// everything listening for typing (the live preview, the dirty indicator, the
// suggestion pool) reacts to a pick exactly as it does to a keystroke.
//
// Suggestions are seeded from the server as the distinct values this
// jurisdiction has already used for the variable. The parent variation-group
// controller adds to them as the admin types elsewhere on the screen, so row 2
// can offer what was just typed in row 1.
export default class extends Controller {
  static targets = [
    'input',
    'list',
    'options',
    'sectionLabel',
    'newOption',
    'newOptionLabel'
  ]
  static values = {
    suggestions: Array,
    open: { type: Boolean, default: false }
  }

  connect() {
    this.highlighted = -1

    if (!this.listTarget.id) {
      this.listTarget.id = `combobox-list-${Math.random().toString(36).slice(2, 10)}`
    }
    this.inputTarget.setAttribute('role', 'combobox')
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.inputTarget.setAttribute('aria-autocomplete', 'list')
    this.inputTarget.setAttribute('aria-controls', this.listTarget.id)
  }

  // --- open / close ---------------------------------------------------------

  toggle(event) {
    event.preventDefault()

    if (this.openValue) {
      this.openValue = false
      return
    }

    this.inputTarget.focus()
    this.showAll = true
    this.openValue = true
  }

  onFocus() {
    this.openValue = true
  }

  onInput() {
    if (this.suppressOpen) return

    this.showAll = false
    this.openValue = true
    this.render()
  }

  clickOutside(event) {
    if (this.element.contains(event.target)) return

    this.openValue = false
  }

  openValueChanged() {
    this.listTarget.classList.toggle('hidden', !this.openValue)
    this.inputTarget.setAttribute('aria-expanded', String(this.openValue))

    if (this.openValue) {
      this.render()
    } else {
      this.showAll = false
      this.highlighted = -1
    }
  }

  // --- placement ------------------------------------------------------------

  // The list is `position: fixed` so the table card's `overflow-hidden` can't
  // clip it, which means its coordinates have to be set by hand: under the input
  // normally, above it when there isn't room below, and nudged left so a
  // right-hand column's dropdown stays on screen.
  reposition() {
    if (!this.openValue) return

    const anchor = this.element.getBoundingClientRect()
    const list = this.listTarget

    list.style.minWidth = `${anchor.width}px`

    const { width, height } = list.getBoundingClientRect()
    const roomBelow = window.innerHeight - anchor.bottom
    const flip = roomBelow < height + 8 && anchor.top > height + 8

    list.style.left = `${Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8))}px`
    list.style.top = flip
      ? `${anchor.top - height + 2}px`
      : `${anchor.bottom - 2}px`
  }

  suggestionsValueChanged() {
    if (this.openValue) this.render()
  }

  // --- keyboard -------------------------------------------------------------

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.openValue = true
        this.moveHighlight(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.moveHighlight(-1)
        break
      case 'Enter': {
        if (!this.openValue) return
        // Enter takes the highlighted suggestion, or the typed text when nothing
        // is highlighted — never a surprise value the admin didn't look at.
        event.preventDefault()
        const option = this.optionElements[this.highlighted]
        this.commit(option ? option.dataset.value : this.inputTarget.value)
        break
      }
      case 'Escape':
        if (!this.openValue) return
        event.preventDefault()
        this.openValue = false
        break
      case 'Tab':
        this.openValue = false
        break
    }
  }

  moveHighlight(step) {
    const count = this.optionElements.length
    if (count === 0) return

    this.highlighted = (this.highlighted + step + count) % count
    this.paintHighlight()
  }

  // --- choosing -------------------------------------------------------------

  choose(event) {
    event.preventDefault()
    this.commit(event.currentTarget.dataset.value)
  }

  chooseTyped(event) {
    event.preventDefault()
    this.commit(this.inputTarget.value)
  }

  commit(value) {
    this.inputTarget.value = value
    this.openValue = false

    // The event has to look like typing, but must not re-open the list.
    this.suppressOpen = true
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.suppressOpen = false
  }

  // --- rendering ------------------------------------------------------------

  render() {
    const typed = this.inputTarget.value.trim()
    const query = this.showAll ? '' : typed.toLowerCase()
    const matches = this.suggestionsValue.filter((value) =>
      value.toLowerCase().includes(query)
    )

    this.optionsTarget.replaceChildren(
      ...matches.map((value) => this.buildOption(value))
    )
    this.sectionLabelTarget.classList.toggle('hidden', matches.length === 0)

    // Only offer "use what I typed" when it isn't already on the list.
    const isNew =
      typed.length > 0 &&
      !this.suggestionsValue.some(
        (value) => value.toLowerCase() === typed.toLowerCase()
      )
    this.newOptionTarget.classList.toggle('hidden', !isNew)
    this.newOptionLabelTarget.textContent = typed

    this.highlighted = matches.length > 0 ? 0 : -1
    this.paintHighlight()

    // The list just changed height, so where it should sit may have too.
    this.reposition()
  }

  buildOption(value) {
    const option = document.createElement('li')
    option.textContent = value
    option.dataset.value = value
    option.setAttribute('role', 'option')
    option.setAttribute('aria-selected', 'false')
    option.className =
      'cursor-pointer rounded-md px-[9px] py-[7px] text-[12.5px] font-medium text-gray-900'
    // mousedown, not click: the input blurs before click would land.
    option.setAttribute('data-action', 'mousedown->combobox#choose')
    return option
  }

  paintHighlight() {
    this.optionElements.forEach((option, index) => {
      const active = index === this.highlighted
      option.classList.toggle('bg-indigo-50', active)
      option.setAttribute('aria-selected', String(active))
    })
  }

  get optionElements() {
    return Array.from(this.optionsTarget.children)
  }
}
